/* Mascotte COSYFUR — « animation chat curseur »
   Rig « la tête suit le curseur » : les sommets au-dessus du cou pivotent
   dans le vertex shader (poids smoothstep), le corps reste immobile. */
(() => {
  const stage = document.getElementById('mascotteStage');
  if (!stage) return;
  const canvas = document.getElementById('mascotteCanvas');
  const fallbackImg = stage.querySelector('.mascotte-fallback');
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // --- bulle BD : effet machine à écrire au premier passage ---
  const bulle = document.getElementById('mascotteBulle');
  const L1 = (bulle && bulle.dataset.l1) || 'Miaou ! Moi c’est Yumi \u{1F43E}';
  const L2 = (bulle && bulle.dataset.l2) || 'Mascotte officielle et premier testeur de la maison COSYFUR. Bouge la souris (ou ton doigt !)… je te suis du regard. Puis descends : je te fais visiter ma fontaine en céramique pièce par pièce.';

  function typewrite(el, text, speed) {
    return new Promise((done) => {
      const caret = document.createElement('span');
      caret.className = 'caret';
      el.textContent = '';
      el.appendChild(caret);
      let i = 0;
      (function tick() {
        if (i < text.length) {
          caret.before(text[i++]);
          setTimeout(tick, speed);
        } else { caret.remove(); done(); }
      })();
    });
  }

  let spoke = false;
  async function speak() {
    if (spoke) return;
    spoke = true;
    bulle.classList.add('visible');
    const l1 = document.getElementById('mascotteL1');
    const l2 = document.getElementById('mascotteL2');
    if (REDUCED) { l1.textContent = L1; l2.textContent = L2; return; }
    await new Promise(r => setTimeout(r, 450));
    await typewrite(l1, L1, 42);
    await typewrite(l2, L2, 17);
  }
  new IntersectionObserver((es, o) => {
    if (es.some(e => e.isIntersecting)) { o.disconnect(); speak(); }
  }, { threshold: 0.45 }).observe(bulle);

  // --- chat 3D : init différée (rien ne se charge tant qu'on est loin) ---
  new IntersectionObserver((es, o) => {
    if (es.some(e => e.isIntersecting)) {
      o.disconnect();
      init().catch((err) => {
        console.warn('mascotte: repli image fixe —', err && err.message);
        canvas.remove();
        if (fallbackImg) fallbackImg.hidden = false;
      });
    }
  }, { rootMargin: '420px' }).observe(stage);

  async function init() {
    if (!window.WebGLRenderingContext) throw new Error('WebGL indisponible');
    const THREE = await import('https://cdn.jsdelivr.net/npm/three@0.170.0/+esm');
    const { GLTFLoader } = await import('https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/loaders/GLTFLoader.js/+esm');
    const { DRACOLoader } = await import('https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/loaders/DRACOLoader.js/+esm');

    const NECK_LOW = -0.22, NECK_HIGH = 0.10;
    const PIVOT = 'vec3(0.0, -0.06, 0.03)';
    const MAX_YAW = 0.55, MAX_PITCH = 0.28;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NeutralToneMapping;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 20);
    camera.position.set(0, 0.18, 3.6);
    camera.lookAt(0, -0.02, 0);

    scene.add(new THREE.HemisphereLight(0xffffff, 0xe8ddca, 1.15));
    const dir = new THREE.DirectionalLight(0xffffff, 1.9);
    dir.position.set(2.2, 3.5, 3.0);
    scene.add(dir);
    const fill = new THREE.DirectionalLight(0xfff2e0, 0.5);
    fill.position.set(-2.5, 1.0, 1.5);
    scene.add(fill);

    const uYaw = { value: 0 }, uPitch = { value: 0 };

    function patchMaterial(mat) {
      mat.onBeforeCompile = (sh) => {
        sh.uniforms.uYaw = uYaw;
        sh.uniforms.uPitch = uPitch;
        const decl = `
          uniform float uYaw;
          uniform float uPitch;
          float headW(float y) { return smoothstep(${NECK_LOW}, ${NECK_HIGH}, y); }
          mat3 headRot(float w) {
            float cy = cos(uYaw * w),   sy = sin(uYaw * w);
            float cx = cos(uPitch * w), sx = sin(uPitch * w);
            mat3 RY = mat3(cy, 0., -sy,  0., 1., 0.,  sy, 0., cy);
            mat3 RX = mat3(1., 0., 0.,  0., cx, sx,  0., -sx, cx);
            return RY * RX;
          }
        `;
        sh.vertexShader = decl + sh.vertexShader
          .replace('#include <beginnormal_vertex>', `
            #include <beginnormal_vertex>
            objectNormal = headRot(headW(position.y)) * objectNormal;
          `)
          .replace('#include <begin_vertex>', `
            #include <begin_vertex>
            {
              vec3 piv = ${PIVOT};
              transformed = headRot(headW(position.y)) * (transformed - piv) + piv;
            }
          `);
      };
      mat.needsUpdate = true;
    }

    const loader = new GLTFLoader();
    const draco = new DRACOLoader();
    draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
    loader.setDRACOLoader(draco);
    await new Promise((resolve, reject) => {
      loader.load(stage.dataset.glb, (gltf) => {
        gltf.scene.traverse((o) => {
          if (o.isMesh) (Array.isArray(o.material) ? o.material : [o.material]).forEach(patchMaterial);
        });
        scene.add(gltf.scene);
        // recadrage auto : tout le chat visible (pattes + oreilles) avec une marge d'air
        const box = new THREE.Box3().setFromObject(gltf.scene);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        gltf.scene.position.sub(center);
        const vfov = camera.fov * Math.PI / 180;
        const dist = (Math.max(size.x, size.y) * 1.34) / (2 * Math.tan(vfov / 2));
        camera.position.set(0, size.y * 0.03, dist);
        camera.lookAt(0, 0, 0);
        resolve();
      }, undefined, reject);
    });

    let targetYaw = 0, targetPitch = 0, lastInput = 0;
    function aim(cx, cy) {
      const r = canvas.getBoundingClientRect();
      const nx = Math.max(-1, Math.min(1, (cx - (r.left + r.width / 2)) / (innerWidth * 0.5)));
      const ny = Math.max(-1, Math.min(1, ((r.top + r.height / 2) - cy) / (innerHeight * 0.6)));
      targetYaw = MAX_YAW * nx;
      targetPitch = -MAX_PITCH * ny;
      lastInput = performance.now();
    }
    addEventListener('mousemove', (e) => aim(e.clientX, e.clientY), { passive: true });
    addEventListener('touchmove', (e) => { const t = e.touches[0]; if (t) aim(t.clientX, t.clientY); }, { passive: true });
    addEventListener('touchstart', (e) => { const t = e.touches[0]; if (t) aim(t.clientX, t.clientY); }, { passive: true });

    function resize() {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      if (w && h) { renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix(); }
    }
    addEventListener('resize', resize);
    resize();

    const loop = (t) => {
      if (!REDUCED && performance.now() - lastInput > 3000) {
        targetYaw = 0.30 * Math.sin(t * 0.0006);
        targetPitch = 0.10 * Math.sin(t * 0.00041 + 1.3);
      }
      uYaw.value += (targetYaw - uYaw.value) * 0.09;
      uPitch.value += (targetPitch - uPitch.value) * 0.09;
      renderer.render(scene, camera);
    };

    // rendu seulement quand la section est à l'écran (batterie/CPU)
    new IntersectionObserver((es) => {
      renderer.setAnimationLoop(es.some(e => e.isIntersecting) ? loop : null);
    }).observe(stage);
    renderer.setAnimationLoop(loop);
    canvas.classList.add('ready');
  }
})();
